import { type ChangeEvent, useEffect, useRef } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/shadcn/select'
import { Avatar } from '@/components/ui/shadcn/avatar'
import { Textarea } from '../ui/shadcn/textarea'
import { type Accounts } from '@backend/lib/db/schema/platform'
import { Button } from './shadcn/button'
import { type PostContent as LinkedInPostContent } from '@backend/lib/platforms/linkedin'
import { multiImageEntryUrl } from '@backend/lib/platforms/linkedin/multiImage'
import { PdfPreview } from './PdfPreview'

type Props = {
  accounts: Accounts | undefined
  selectedAccountId: string | undefined
  postContent: LinkedInPostContent
  onChangeCaption?: (e: ChangeEvent<HTMLTextAreaElement>) => void
  onChangeAccountId: (accountId: string) => void
  onStartAuth: () => void
}

export function LinkedinPostPreview({
  accounts,
  selectedAccountId,
  postContent,
  onChangeCaption,
  onChangeAccountId,
  onStartAuth,
}: Props) {
  // ref
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [postContent])

  const onAccountChange = (accountId: string) => {
    // Don't set local state, just call the callback
    onChangeAccountId(accountId)
  }

  return (
    <div className="text-foreground mx-auto flex h-full w-full flex-col gap-4 rounded-lg p-4 pb-0">
      {accounts && accounts.length > 0 ? (
        // MULTIPLE ACCOUNTS
        <div className="flex items-center gap-2">
          <Select value={selectedAccountId} onValueChange={onAccountChange}>
            <SelectTrigger className="text-foreground border-none p-0 shadow-none">
              {selectedAccountId ? (
                <ProfileHeader
                  imageUrl={accounts.find((account) => account.id === selectedAccountId)!.imageUrl}
                  displayName={
                    accounts.find((account) => account.id === selectedAccountId)!.displayName
                  }
                  subline={accounts.find((account) => account.id === selectedAccountId)!.subline}
                />
              ) : (
                <ProfileHeader
                  imageUrl={undefined}
                  displayName="Select account"
                  subline={undefined}
                />
              )}
            </SelectTrigger>
            <SelectContent className="flex w-full flex-col gap-12 rounded-none border-none bg-gray-200">
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  <ProfileHeader
                    imageUrl={account.imageUrl}
                    displayName={account.displayName}
                    subline={account.subline}
                  />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        // NOT AUTHENTICATED
        <div className="flex items-center justify-between">
          <ProfileHeader
            imageUrl={undefined}
            displayName="Not authenticated"
            subline="Head of Marketing @ Company"
          />
          <Button onClick={onStartAuth}>Connect</Button>
        </div>
      )}

      {/* Post text */}
      <div>
        <Textarea
          ref={textareaRef}
          className="resize-none whitespace-pre-wrap border-none p-0 text-xs"
          placeholder="What do you want to talk about?"
          value={postContent?.caption || ''}
          onChange={onChangeCaption}
        />
      </div>

      {/* Preview image / video */}
      <div className="-mx-4">
        {(() => {
          switch (postContent?.type) {
            case 'text':
            case 'reshare':
            case 'article':
            case 'poll':
              return null

            case 'image':
              return (
                <img
                  src={postContent?.url || ''}
                  crossOrigin="anonymous"
                  className="h-auto w-full object-cover"
                />
              )

            case 'multiImage': {
              const images = (postContent?.urls || []).map(multiImageEntryUrl)
              const imageCount = images.length

              if (imageCount === 0) return null

              if (imageCount === 1) {
                return (
                  <img
                    src={images[0]}
                    crossOrigin="anonymous"
                    className="h-auto w-full object-cover"
                  />
                )
              }

              if (imageCount === 2) {
                return (
                  <div className="flex gap-1">
                    {images.map((url) => (
                      <img
                        key={url}
                        src={url}
                        crossOrigin="anonymous"
                        className="h-auto max-h-[300px] min-w-0 flex-1 object-cover"
                      />
                    ))}
                  </div>
                )
              }

              // 3+ images: first image large, then grid of remaining
              const firstImage = images[0]
              const remainingImages = images.slice(1)
              const maxVisibleRemaining = 2
              const visibleRemaining = remainingImages.slice(0, maxVisibleRemaining)
              const hiddenCount = remainingImages.length - maxVisibleRemaining

              return (
                <div className="flex flex-col gap-1">
                  {/* First image - large */}
                  <img
                    src={firstImage}
                    crossOrigin="anonymous"
                    className="h-auto max-h-[300px] w-full object-cover"
                  />

                  {/* Remaining images in grid */}
                  {visibleRemaining.length > 0 && (
                    <div className="flex gap-1">
                      {visibleRemaining.map((url, index) => (
                        <div key={url} className="relative flex-1">
                          <img
                            src={url}
                            crossOrigin="anonymous"
                            className="h-auto max-h-[150px] w-full object-cover"
                          />
                          {/* Show +X overlay on last visible image if there are hidden images */}
                          {index === visibleRemaining.length - 1 && hiddenCount > 0 && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                              <span className="text-2xl font-semibold text-white">
                                +{hiddenCount}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            }

            case 'video':
              return (
                <video
                  src={postContent?.url || ''}
                  className="h-auto w-full object-cover"
                  controls
                  autoPlay
                  muted
                />
              )

            case 'document':
              return <PdfPreview src={postContent?.url || ''} />
          }
        })()}
      </div>
    </div>
  )
}

const ProfileHeader = ({
  imageUrl,
  displayName,
  subline,
}: {
  imageUrl: string | undefined
  displayName: string
  subline: string | undefined
}) => {
  return (
    <div className="flex items-center gap-2">
      <ProfilePicture imageUrl={imageUrl} />
      <div className="text-left">
        <p className="text-foreground text-[14px] font-semibold">{displayName}</p>
        {subline && <p className="text-[12px] text-gray-500">{subline}</p>}
      </div>
    </div>
  )
}

const ProfilePicture = ({ imageUrl }: { imageUrl: string | undefined }) => {
  return (
    <Avatar>
      {imageUrl ? (
        <div className="bg-background border-border flex h-full w-full items-center justify-center rounded-full border">
          <img src={imageUrl} />
        </div>
      ) : (
        <div className="w-full bg-gray-400"></div>
      )}
    </Avatar>
  )
}
